import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { phoneVerifyState, phoneVerifySend, phoneVerifyCheck } from './auth';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};
const PV = cfg.phoneVerify || {};

/**
 * Normalise to the same international digits the server uses (cn_pv_norm), so the
 * client and the gate agree on what "this number" means. Mobiles only — an SMS
 * can't land on a landline, and COD verification is the whole point.
 * Returns '' when it isn't a GR (69…) or CY (9…) mobile.
 */
export function normMobile(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.indexOf('00') === 0) d = d.slice(2);
  if (d.length === 12 && d.indexOf('30') === 0) d = d.slice(2);
  else if (d.length === 11 && d.indexOf('357') === 0) d = d.slice(3);
  if (/^69\d{8}$/.test(d)) return `30${d}`;
  if (/^9\d{7}$/.test(d)) return `357${d}`;
  return '';
}

/** Strip the country code back off — the form the customer recognises. */
export function nationalMobile(normalised) {
  if (normalised.indexOf('30') === 0) return normalised.slice(2);
  if (normalised.indexOf('357') === 0) return normalised.slice(3);
  return normalised;
}

/** Does this payment method require a verified mobile? */
export function needsPhoneVerify(paymentId) {
  if (!PV.enabled) return false;
  const gated = PV.methods || ['cod'];
  return gated.indexOf(paymentId) !== -1;
}

/**
 * How to name the chosen method inside a sentence about verification — the mirror of
 * cn_pv_method_label() in phone-verify.php. Two sentences say this (the panel and the
 * place-order blocker) and they must say the same thing as the server's refusal, or a
 * pickup customer is told to verify for αντικαταβολή they never picked.
 */
export function verifyMethodLabel(paymentId) {
  return paymentId === 'cod' ? 'την αντικαταβολή' : 'την πληρωμή κατά την παραλαβή';
}

function phCapture(event, props) {
  try {
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, { checkout_flow: 'new', ...props });
    }
  } catch (e) { /* analytics must never block checkout */ }
}

const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

/**
 * Mobile verification for the pay-later methods — expands under the chosen row
 * (αντικαταβολή, or πληρωμή κατά την παραλαβή).
 *
 *   idle → a link that SMSes a code to the contact number from step 1
 *   code → no button; auto-submits the moment 6 digits are in the field
 *   done → green «Το τηλέφωνο επιβεβαιώθηκε» tick
 *
 * There is deliberately NO phone field here: the number was already collected in
 * step 1, and asking for it twice invites a typo that splits the order's contact
 * number from the verified one. Editing it means going back to step 1, which this
 * panel follows automatically.
 *
 * The code field is a single input with autocomplete="one-time-code", which is what
 * makes iOS offer «Paste 123456 from Messages» above the keyboard. Android gets the
 * same one-tap via WebOTP (navigator.credentials.get) — that needs the SMS's last
 * line to be `@<our host> #<code>`, which cn_pv_send_sms writes.
 *
 * @param {string}   phone      Contact phone from step 1 — the number we verify.
 * @param {boolean}  verified   Whether `phone` is already verified.
 * @param {function} onVerified Called with the normalised number once verified.
 * @param {string}   method     Gateway id, so the copy names the right method.
 */
export default function CodPhoneVerify({ phone, verified, onVerified, method }) {
  const [phase, setPhase] = useState(verified ? 'done' : 'idle');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);
  const submitting = useRef(false); // guards WebOTP + onChange racing to submit
  const probed = useRef('');        // last number we asked the server about

  const normalised = normMobile(phone);
  const display = normalised ? nationalMobile(normalised) : '';

  // Follow the parent: verified flips us to the tick, a step-1 edit reopens the panel.
  useEffect(() => { setPhase(verified ? 'done' : 'idle'); }, [verified]);

  // A number verified before (another order, another device, or an SMS login) skips
  // the code entirely — that's the "verify once, ever" promise.
  useEffect(() => {
    if (phase !== 'idle' || !normalised || probed.current === normalised) return undefined;
    probed.current = normalised;
    let stale = false;
    phoneVerifyState(normalised).then(({ ok, data }) => {
      if (stale || !ok || !data.verified) return;
      setPhase('done');
      onVerified(normalised);
    });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalised, phase]);

  // Resend countdown.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = useCallback(async (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 6);
    if (digits.length !== 6 || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    const { ok, data } = await phoneVerifyCheck(normalised, digits);
    setBusy(false);
    submitting.current = false;
    if (ok && data.verified) {
      setPhase('done');
      onVerified(normalised);
      phCapture('cod_phone_verified', {});
      return;
    }
    setCode('');
    setError(data.message || 'Λάθος κωδικός.');
    if (data.expired) setPhase('idle'); // code burned — request a new one
    phCapture('cod_phone_verify_failed', { reason: data.expired ? 'expired' : 'wrong_code' });
    if (codeRef.current) codeRef.current.focus();
  }, [normalised, onVerified]);

  // Android one-tap: Chrome hands us the code straight out of the SMS.
  //
  // Subscribed strictly ONCE per code phase. `verify` is reached through a ref
  // rather than the dep array on purpose: re-running this effect would abort and
  // re-issue the credential request mid-flight, killing the browser's SMS prompt.
  const verifyRef = useRef(verify);
  useEffect(() => { verifyRef.current = verify; }, [verify]);
  useEffect(() => {
    if (phase !== 'code') return undefined;
    if (typeof window === 'undefined' || !('OTPCredential' in window) || !navigator.credentials) return undefined;
    let ac;
    try { ac = new AbortController(); } catch (e) { return undefined; }
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((otp) => {
        if (!otp || !otp.code) return;
        setCode(otp.code.replace(/\D/g, '').slice(0, 6));
        verifyRef.current(otp.code);
      })
      .catch(() => { /* dismissed / unsupported — the keyboard suggestion still works */ });
    return () => { try { ac.abort(); } catch (e) { /* noop */ } };
  }, [phase]);

  /**
   * Open the code step FIRST, then send — order matters on mobile.
   *
   * A phone keyboard only opens for a focus() that happens inside the user's own
   * tap. Awaiting the send (1-3s) spends that gesture, so focusing afterwards is
   * silently ignored and the customer has to tap the field themselves. Switching
   * phase here is a discrete event, so React flushes the render synchronously and
   * the layout effect below focuses the field while the tap is still live.
   * The send runs unawaited behind it and rolls the phase back if it fails.
   */
  function startVerify() {
    if (!normalised || busy || cooldown > 0) return;
    setCode('');
    setError('');
    setPhase('code');
    send(false);
  }

  async function send(isResend) {
    if (!normalised) return;
    setBusy(true);
    setError('');
    const { ok, data } = await phoneVerifySend(normalised);
    setBusy(false);
    if (ok && data.verified) { // already known — no SMS needed
      setPhase('done');
      onVerified(normalised);
      return;
    }
    if (!ok) {
      setError(data.message || 'Δεν ήταν δυνατή η αποστολή του SMS.');
      if (data.cooldown) setCooldown(data.cooldown);
      if (!isResend) setPhase('idle'); // nothing was sent — undo the optimistic step
      return;
    }
    setCooldown(data.cooldown || PV.cooldown || 60);
    phCapture('cod_phone_code_sent', { resend: !!isResend });
  }

  // Runs inside the tap that opened the code step (see startVerify) → the mobile
  // keyboard comes up on its own, with «Paste from Messages» ready above it.
  // Scrolling is deliberately NOT prevented here: the browser's own scroll-into-view
  // is the first line of defence against the keyboard covering the field.
  useLayoutEffect(() => {
    if (phase !== 'code' || !codeRef.current) return;
    codeRef.current.focus();
  }, [phase]);

  // …and the second line of defence. The keyboard doesn't resize the layout
  // viewport, so the browser can happily leave the field underneath it.
  //
  // This corrects rather than predicts, which is what stops it both oscillating
  // AND giving up:
  //   - ONE rule, one direction: scroll only when the field is actually below the
  //     usable area. Never scroll it back down. Two competing rules is what made
  //     the first version ping-pong.
  //   - INSTANT scroll, not smooth: a smooth scroll is still moving when the next
  //     resize arrives, so the follow-up measures a moving target and overshoots.
  //   - Re-checked on a short schedule (the keyboard animates in over ~300ms and
  //     browsers report the new viewport at wildly different moments) plus on
  //     resize. Every pass is idempotent and each one strictly reduces the
  //     obstruction, so it converges and then does nothing. The earlier
  //     "only act if the height changed" guard silently swallowed the one event
  //     that mattered whenever it collided with the settle lock — hence the field
  //     ending up under the keyboard again.
  useEffect(() => {
    if (phase !== 'code' || typeof window === 'undefined') return undefined;
    const vv = window.visualViewport || null;

    // How far the field's bottom sits below the usable area. >0 means obscured.
    const overshoot = () => {
      const el = codeRef.current;
      if (!el) return 0;
      const viewTop = vv ? vv.offsetTop : 0;   // iOS shifts the visual viewport
      const viewH = vv ? vv.height : window.innerHeight;

      // The mobile totals bar is position:fixed. On Android it sits on top of the
      // keyboard and really does steal space; on iOS it's pinned to the LAYOUT
      // viewport, i.e. behind the keyboard and invisible. Measuring the overlap
      // with the visible area instead of blindly subtracting its height gets both
      // right — off-screen bars contribute 0.
      let obstacle = 0;
      const bar = document.querySelector('.cn-sum--mobile');
      if (bar) {
        const cs = window.getComputedStyle(bar);
        if (cs.display !== 'none' && cs.position === 'fixed') {
          obstacle = Math.max(0, viewH - (bar.getBoundingClientRect().top - viewTop));
        }
      }

      const rect = el.getBoundingClientRect();
      const topInView = rect.top - viewTop;
      const bottomInView = topInView + rect.height;
      const delta = bottomInView - (viewH - obstacle - 16);
      // Never scroll so far that the field leaves the top of the screen.
      return Math.min(delta, Math.max(0, topInView - 8));
    };

    const nudge = () => {
      const delta = overshoot();
      if (delta > 8) window.scrollBy({ top: delta, behavior: 'auto' });
    };

    // Browsers report the post-keyboard viewport anywhere in this window.
    const timers = [120, 300, 600, 1000].map((ms) => window.setTimeout(nudge, ms));
    let debounce = null;
    const onResize = () => { window.clearTimeout(debounce); debounce = window.setTimeout(nudge, 120); };
    if (vv) vv.addEventListener('resize', onResize);
    return () => {
      timers.forEach(window.clearTimeout);
      window.clearTimeout(debounce);
      if (vv) vv.removeEventListener('resize', onResize);
    };
  }, [phase]);

  if (phase === 'done') {
    return (
      <div className="cn-pv cn-pv--done" id="cn-pv-panel">
        {/* Check is inline in the sentence, not in its own column, so this line
            starts on the same left edge as the fee note and the send link. */}
        <span className="cn-pv__doneTitle"><IconCheck /> Το τηλέφωνο επιβεβαιώθηκε</span>
        {display && <span className="cn-pv__num">{display}</span>}
      </div>
    );
  }

  // No usable mobile in step 1 — an SMS has nowhere to go, so point them back
  // rather than opening a second phone field here.
  if (!normalised) {
    return (
      <div className="cn-pv" id="cn-pv-panel">
        <p className="cn-pv__intro">
          Για {verifyMethodLabel(method)} χρειαζόμαστε αριθμό κινητού. Πάτησε <strong>Πίσω</strong> και
          συμπλήρωσε ένα κινητό στα στοιχεία επικοινωνίας.
        </p>
      </div>
    );
  }

  return (
    <div className="cn-pv" id="cn-pv-panel">
      {phase === 'idle' && (
        <button
          type="button"
          className="cn-link cn-pv__sendlink"
          disabled={cooldown > 0}
          onClick={startVerify}
        >
          {cooldown > 0 ? `Νέο SMS σε ${cooldown}s` : 'Επιβεβαίωσε το κινητό σου εδώ'}
        </button>
      )}

      {phase === 'code' && (
        <>
          <p className="cn-pv__intro">
            {busy && !code
              ? <>Στέλνουμε 6ψήφιο κωδικό στο <strong>{display}</strong>…</>
              : <>Στείλαμε 6ψήφιο κωδικό στο <strong>{display}</strong>. Συμπλήρωσέ τον — επιβεβαιώνεται αυτόματα.</>}
          </p>
          <input
            ref={codeRef}
            type="text"
            name="otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            className="cn-input cn-input--code cn-pv__code"
            placeholder="______"
            aria-label="Κωδικός επιβεβαίωσης"
            /* NOT disabled while busy: a disabled field can't hold focus, and losing
               it would shut the keyboard we just opened. */
            value={code}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(v);
              setError('');
              if (v.length === 6) verify(v); // no button — the OTP submits itself
            }}
          />
          <div className="cn-pv__actions">
            <button type="button" className="cn-link" disabled={cooldown > 0 || busy} onClick={() => send(true)}>
              {cooldown > 0 ? `Νέο SMS σε ${cooldown}s` : 'Στείλε ξανά'}
            </button>
            {busy && code.length === 6 && <span className="cn-pv__hint">Επαλήθευση…</span>}
          </div>
        </>
      )}

      {error && <p className="cn-afm-error" role="alert">{error}</p>}
    </div>
  );
}
